import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEO from '@/components/feature/SEO';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === id);

  const formatViews = (v: number) => {
    if (v >= 10000) return (v / 10000).toFixed(1) + '万';
    return v.toString();
  };

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center bg-background-50">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-foreground-950 mb-4">文章不存在</h1>
            <Link to="/" className="text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.id !== article.id)
    .slice(0, 3);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Article',
        headline: article.title,
        description: article.excerpt,
        url: `https://www.chiguavideo.com/article/${article.id}`,
        datePublished: article.date,
        author: {
          '@type': 'Person',
          name: article.author,
        },
        publisher: {
          '@type': 'Organization',
          name: '吃瓜视频网',
          url: 'https://www.chiguavideo.com',
          logo: {
            '@type': 'ImageObject',
            url: 'https://www.chiguavideo.com/logo.png',
          },
        },
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': `https://www.chiguavideo.com/article/${article.id}`,
        },
        image: article.imageUrl,
        articleSection: article.categoryName,
        keywords: article.tags.join(', '),
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: '首页',
            item: 'https://www.chiguavideo.com/',
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: article.categoryName,
            item: `https://www.chiguavideo.com/category/${article.category}`,
          },
          {
            '@type': 'ListItem',
            position: 3,
            name: article.title,
            item: `https://www.chiguavideo.com/article/${article.id}`,
          },
        ],
      },
    ],
  };

  return (
    <>
      <SEO
        title={`${article.title} - 吃瓜视频网`}
        description={article.excerpt}
        keywords={`${article.tags.join(',')},吃瓜视频,吃瓜视频在线播放,在线观看吃瓜视频`}
        canonicalUrl={`https://www.chiguavideo.com/article/${article.id}`}
        ogTitle={article.title}
        ogDescription={article.excerpt}
        ogUrl={`https://www.chiguavideo.com/article/${article.id}`}
        ogType="article"
        jsonLd={jsonLd}
      />

      <Navbar />

      <main className="pt-16 bg-background-50">
        <article>
          <header className="w-full bg-background-50">
            <div className="w-full px-4 md:px-6 pt-8 pb-6">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: article.categoryName, href: `/category/${article.category}` },
                  { label: article.title },
                ]}
              />
            </div>

            <div className="w-full px-4 md:px-6 pb-8">
              <div className="flex flex-col lg:flex-row lg:items-start gap-8 lg:gap-12">
                <div className="lg:w-1/2">
                  <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-[1.15]">
                    {article.title}
                  </h1>
                </div>
                <div className="lg:w-1/2 lg:pt-2">
                  <p className="text-base text-foreground-600 leading-relaxed mb-5 max-w-md">
                    {article.excerpt}
                  </p>
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="flex items-center gap-1.5 text-sm text-foreground-500">
                      <i className="ri-eye-line"></i>
                      {formatViews(article.views)} 次阅读
                    </span>
                    <span className="flex items-center gap-1.5 text-sm text-foreground-500">
                      <i className="ri-chat-3-line"></i>
                      {formatViews(article.comments)} 条评论
                    </span>
                    <span className="flex items-center gap-1.5 text-sm text-foreground-500">
                      <i className="ri-thumb-up-line"></i>
                      {formatViews(article.likes)} 赞
                    </span>
                  </div>
                </div>
              </div>

              <div className="mt-8 relative overflow-hidden rounded-2xl h-[300px] md:h-[420px]">
                <img
                  src={article.imageUrl}
                  alt={article.title}
                  className="w-full h-full object-cover object-top"
                  title={article.title}
                />
                <div className="absolute inset-0 bg-gradient-to-b from-black/5 via-transparent to-background-50/90"></div>
              </div>
            </div>
          </header>

          <section className="w-full bg-background-50 py-8">
            <div className="w-full max-w-3xl mx-auto px-4 md:px-6">
              <div className="flex items-center gap-4 text-xs text-foreground-500 mb-10 flex-wrap">
                <div className="flex items-center gap-1.5 bg-background-100 px-3 py-1.5 rounded-lg">
                  <i className="ri-calendar-line"></i>
                  <span>{article.date}</span>
                </div>
                <div className="flex items-center gap-1.5 bg-background-100 px-3 py-1.5 rounded-lg">
                  <i className="ri-user-line"></i>
                  <span>{article.author}</span>
                </div>
                <div className="flex items-center gap-1.5 bg-background-100 px-3 py-1.5 rounded-lg">
                  <i className="ri-price-tag-3-line"></i>
                  <span>{article.tags.join(' · ')}</span>
                </div>
              </div>

              <div className="relative w-full mb-10 rounded-xl overflow-hidden bg-background-50">
                <div className="aspect-video">
                  <iframe
                    src={article.videoUrl}
                    title={article.title}
                    className="w-full h-full"
                    allowFullScreen
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  />
                </div>
              </div>

              <div className="prose prose-lg max-w-none">
                <div className="text-base md:text-lg text-foreground-950 leading-relaxed space-y-5">
                  {article.content.split('\n\n').map((paragraph, i) => (
                    <p key={i}>{paragraph}</p>
                  ))}
                </div>
              </div>

              <div className="mt-10 flex items-center gap-3 flex-wrap">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-4 py-1.5 bg-primary-100 text-primary-600 text-xs font-medium rounded-full whitespace-nowrap"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          </section>

          {relatedArticles.length > 0 && (
            <section className="w-full bg-background-950 py-12 md:py-16">
              <div className="w-full px-4 md:px-6">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-50 mb-8">
                  相关推荐
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                  {relatedArticles.map((rel) => (
                    <Link
                      key={rel.id}
                      to={`/article/${rel.id}`}
                      className="group block bg-background-50 rounded-xl overflow-hidden hover:ring-2 hover:ring-primary-500/50 transition-all duration-300"
                    >
                      <div className="relative h-44 md:h-52 overflow-hidden">
                        <img
                          src={rel.imageUrl}
                          alt={rel.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                          title={rel.title}
                        />
                        <div className="absolute bottom-3 right-3 bg-background-50/80 backdrop-blur-sm rounded-full px-3 py-1 flex items-center gap-1">
                          <i className="ri-play-circle-fill text-primary-500 text-sm"></i>
                          <span className="text-xs text-foreground-950 font-medium">
                            {formatViews(rel.views)}
                          </span>
                        </div>
                      </div>
                      <div className="p-4">
                        <h3 className="text-sm font-bold text-foreground-950 leading-snug line-clamp-2 group-hover:text-primary-500 transition-colors">
                          {rel.title}
                        </h3>
                        <p className="text-xs text-foreground-500 mt-2">{rel.date}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          )}

          <section className="w-full bg-background-900 py-12 md:py-16">
            <div className="w-full max-w-3xl mx-auto px-4 md:px-6">
              <div className="flex items-center gap-2 mb-8">
                <span className="w-2 h-2 rounded-full bg-primary-500"></span>
                <span className="text-xs font-medium text-primary-500 uppercase tracking-wider whitespace-nowrap">网友热议</span>
              </div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-50 mb-2">吃瓜群众怎么说</h2>
              <p className="text-sm text-foreground-600 mb-8">文明吃瓜，理性讨论</p>

              <div className="space-y-4">
                {[
                  { name: '吃瓜一线', time: '2小时前', text: '这也太劲爆了吧！娱乐圈果然是最大的瓜田。不过还是要理性看待，等官方回应。', likes: 1234 },
                  { name: '理性分析家', time: '3小时前', text: '从时间线和逻辑上分析，这件事还有很多疑点。建议大家不要急着站队，让子弹飞一会儿。', likes: 987 },
                  { name: '八卦爱好者', time: '5小时前', text: '我说什么来着！之前就有端倪了，只是当时没人信。现在实锤了吧！', likes: 756 },
                  { name: '路过的吃瓜人', time: '6小时前', text: '每天刷吃瓜视频网已经成为习惯了，信息整理得很清晰，比微博碎片化信息好太多。', likes: 543 },
                ].map((comment, i) => (
                  <div key={i} className="bg-background-50 rounded-xl p-5">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-semibold text-foreground-950">{comment.name}</span>
                      <span className="text-xs text-foreground-500">{comment.time}</span>
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-3">{comment.text}</p>
                    <div className="flex items-center gap-1 text-xs text-foreground-500">
                      <i className="ri-thumb-up-line"></i>
                      <span>{comment.likes}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </article>
      </main>

      <Footer />
    </>
  );
}